import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * Download the whole read as one MP3 (owner/admin). Built on request from the existing takes with the
 * listener's current narration / speaker-name settings, so it sounds like the player; kept a few days.
 *   POST {base}/export {narration, speaker_names} -> {id, status, file_url, mb, minutes, keep_days}
 *   GET  {base}/export/{id}                        -> same, polled while building
 */
export default function Download({ base, settings, retentionDays, onClose }) {
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(null);
  const timer = useRef(null);
  const live = useRef(true);

  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    addEventListener('keydown', k, true); return () => removeEventListener('keydown', k, true);
  }, [onClose]);

  // The poll timer is cleared on UNMOUNT ONLY. It used to hang off the Escape-key effect, whose
  // onClose dependency is a new closure on every parent render — and the player re-renders on every
  // audio time update, so preparing an MP3 while listening cancelled the poll within a tick and the
  // dialog sat on "Preparing…" for ever (review 2026-09-23, finding 6).
  useEffect(() => () => { live.current = false; clearTimeout(timer.current); }, []);

  const poll = (id) => {
    timer.current = setTimeout(() => axios.get(`${base}/export/${id}`).then((r) => {
      if (!live.current) return;
      setJob(r.data);
      if (r.data.status === 'queued' || r.data.status === 'building') poll(id);
    }).catch(() => { if (live.current) poll(id); }), 4000);
  };
  const start = () => {
    setErr(null);
    axios.post(`${base}/export`, settings).then((r) => {
      setJob(r.data);
      if (r.data.status === 'queued' || r.data.status === 'building') poll(r.data.id);
    }).catch((e) => setErr(e?.response?.data?.error ?? e.message));
  };

  const busy = job && (job.status === 'queued' || job.status === 'building');
  const label = { full: 'full narration', none: 'no narration' }[settings.narration] ?? settings.narration;
  const names = { always: 'speaker names before every line', first_per_scene: 'speaker names once per scene', never: 'no speaker names' }[settings.speaker_names];

  return (
    <div className="tr-fb-backdrop" onClick={onClose}>
      <div className="tr-fb" role="dialog" aria-modal="true" aria-labelledby="tr-dl-h" onClick={(e) => e.stopPropagation()}>
        <h2 id="tr-dl-h" className="tr-fb-h">Download the table read</h2>
        <p className="tr-dim">One MP3 of the whole read, with {label} and {names} — the same settings you are listening with.</p>
        <p className="tr-dim">Table reads are kept for {retentionDays} days after the last listen. Download it to keep it.</p>
        {err && <p className="tr-err">{err}</p>}
        {job?.status === 'failed' && <p className="tr-err">{job.error} Please try again, or tell us via Send feedback.</p>}
        {job?.detail && <p className="tr-dim" style={{ fontFamily: 'ui-monospace, monospace', fontSize: '.78rem' }}>{job.detail}</p>}
        {job?.partial && (
          <p className="tr-dim">This read is still recording: the file will hold the {job.scenes} scene{job.scenes === 1 ? '' : 's'} that
            are ready, of {job.total}. Prepare it again when the read is finished to get all of it.</p>
        )}
        {busy && <p className="tr-progress">Preparing the file… a feature-length read takes a few minutes. You can keep listening.</p>}
        <div className="tr-fb-actions">
          <button className="tr-btn" onClick={onClose}>Close</button>
          {job?.status === 'ready'
            ? <a className="tr-btn tr-play" href={job.file_url}>Download MP3{job.mb ? ` · ${job.mb} MB` : ''}</a>
            : <button className="tr-btn tr-play" onClick={start} disabled={busy}>{busy ? 'Preparing…' : 'Prepare MP3'}</button>}
        </div>
        {job?.status === 'ready' && <p className="tr-dim tr-dl-note">This file is kept for {job.keep_days} days{job.minutes ? ` · ${job.minutes} min` : ''}.</p>}
      </div>
    </div>
  );
}
